/**
 * chatbotEngine.js — Re Leve IA (Etapa 6)
 * Motor determinístico real. Sem LLM externa, sem backend, sem banco.
 * Etapa 6: integra jornada comercial, confirmação de kit e plano calórico.
 */

import { detectIntent } from './intentEngine';
import { analyzeGuardrails } from './guardrailsEngine';
import { getCustomerMemoryContext } from './memoryContextEngine';
import { getRecommendedKitForCustomer } from './recommendationEngine';
import {
  hasCalorieConsent,
  grantCalorieConsent,
  denyCalorieConsent,
  requestCalorieConsent,
  getConsentStatus,
} from './consentEngine';
import {
  detectDeclaredRestriction,
  saveDeclaredRestriction,
  getDeclaredRestrictions,
  hasGraveAllergyAlert,
} from './restrictionEngine';
import { addSessionEvent, getSessionEvents } from './localStorageEngine';
import {
  selectResponseVariant,
  getCalorieActiveVariant,
  getRestrictionSavedVariant,
} from './responseVariationEngine';
import {
  getCustomerJourneyStage,
  getNextBestAction,
  getCommercialTriggers,
  createCommercialEvent,
  getJourneySummary,
  getRebuySuggestion,
  getUpsellSuggestion,
  getCrossSellSuggestion,
  COMMERCIAL_EVENT_TYPES,
} from './commercialJourneyEngine';
import {
  confirmKitSelection,
  buildWhatsAppSummary,
  getSimulatedCheckoutState,
} from './orderSimulationEngine';
import {
  createPlanFromConfirmedKit,
  registerMeal,
  CALORIE_ESTIMATES,
  DISCLAIMER,
} from './calorieTrackerEngine';

// ── Resposta de consentimento pendente ────────────────────────────────────────

const CONSENT_YES_PATTERNS = /\b(sim,?\s*autorizo|autorizo|pode\s+seguir|sim|ok,?\s*pode|claro|concordo|aceito)\b/i;
const CONSENT_NO_PATTERNS  = /\b(agora\s+n[aã]o|n[aã]o\s+autorizo|n[aã]o|prefiro\s+n[aã]o|recuso)\b/i;

function isPendingConsentYes(message) { return CONSENT_YES_PATTERNS.test(message); }
function isPendingConsentNo(message)  { return CONSENT_NO_PATTERNS.test(message); }

// ── Detectar tipo de refeição na mensagem ─────────────────────────────────────

function detectMealType(message) {
  const norm = message.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');

  if (/almoc/.test(norm)) return 'almoco';
  if (/jant/.test(norm))  return 'jantar';
  if (/lanch/.test(norm)) return 'snack';
  if (/snack/.test(norm)) return 'snack';
  if (/bebid/.test(norm)) return 'bebida';
  if (/sobrem/.test(norm)) return 'sobremesa';
  if (/comi|consumi/.test(norm)) return 'almoco'; // fallback
  return null;
}

// ── Respostas públicas por intenção ───────────────────────────────────────────

function buildPublicResponse({ intent, customer, kit, memoryContext, guardrailResult, clienteId, segment, lastConfirmedKit }) {
  const seg = segment || (customer?.segmentoComercial || customer?.segmento || customer?.segment || '').toLowerCase();

  const isColetivo   = seg.includes('coletivo');
  const isFiel       = seg.includes('fiel') || seg.includes('alto valor');
  const isNovo       = seg.includes('novo');
  const isRecorrente = seg.includes('recorrente');
  const isInativo    = seg.includes('inativo');

  const localRestrictions = getDeclaredRestrictions(clienteId);
  const hasLocalRestriction = localRestrictions.length > 0;
  const restrictionLabel = hasLocalRestriction ? localRestrictions.map(r => r.label).join(', ') : null;
  const restrictionNote = restrictionLabel ? ` Vou considerar sua restrição (${restrictionLabel}) e confirmar disponibilidade no atendimento.` : '';

  const memRestrictions = memoryContext?.declaredRestrictions?.length > 0;
  const memRestrictionNote = memRestrictions && !hasLocalRestriction
    ? ` Vou considerar sua restrição e confirmar disponibilidade.` : '';

  const finalRestrictionNote = restrictionNote || memRestrictionNote;

  switch (intent) {
    case 'saudacao':
      return selectResponseVariant({ intent: 'saudacao', segment: seg, variantKey: 'greet', customerId: clienteId });

    case 'montar_semana': {
      if (isColetivo) {
        return 'Você está comprando para você, família ou equipe? Posso montar a melhor opção para cada caso e encaminhar para o atendimento adequado.';
      }
      const weekVariant = selectResponseVariant({ intent: 'montar_semana', segment: seg, variantKey: 'week', customerId: clienteId });
      if (kit) {
        if (isFiel)       return `Posso montar a sua ${kit.kitName} com almoço, jantar, snack e bebida para os 7 dias. Quer que eu confirme esse kit e prepare o resumo para WhatsApp?${finalRestrictionNote}`;
        if (isRecorrente) return `Posso montar uma ${kit.kitName} com almoço, jantar, snack e bebida para os 5 dias úteis. Quer confirmar?${finalRestrictionNote}`;
        if (isNovo || isInativo) return `Que tal começar com uma ${kit.kitName}? É prático, econômico e resolve os almoços da semana. Quer confirmar esse kit?`;
        return `Posso montar uma ${kit.kitName} para facilitar sua rotina. Quer que eu confirme esse kit e prepare o resumo para WhatsApp?${finalRestrictionNote}`;
      }
      return `${weekVariant}${finalRestrictionNote}`;
    }

    case 'kit_dia': {
      if (isColetivo) return 'Para um kit do dia coletivo, preciso saber a quantidade de pessoas. Você está comprando para quantas pessoas?';
      const kitVariant = selectResponseVariant({ intent: 'kit_dia', segment: seg, variantKey: 'day', customerId: clienteId });
      if (kit) return `${kit.kitName}: ${(kit.items || []).slice(0, 3).join(', ')}. Tudo em um único pedido, sem complicação. Quer confirmar esse kit?${finalRestrictionNote}`;
      return `${kitVariant}${finalRestrictionNote}`;
    }

    case 'confirmar_kit': {
      const kitToConfirm = lastConfirmedKit || kit;
      if (!kitToConfirm) return 'Qual kit você gostaria de confirmar? Posso sugerir um de acordo com o que você precisa!';
      const calorieConsent = clienteId ? hasCalorieConsent(clienteId) : false;
      if (calorieConsent) {
        return `Perfeito! Confirmei o *${kitToConfirm.kitName}* e já criei seu plano calórico informativo. Montei o resumo para finalizar pelo WhatsApp. ${DISCLAIMER}`;
      }
      return `Perfeito! Montei um resumo do seu *${kitToConfirm.kitName}* para finalizar pelo WhatsApp. Posso incluir também o acompanhamento calórico informativo, caso você autorize.`;
    }

    case 'registrar_refeicao': {
      const consentActive = hasCalorieConsent(clienteId);
      if (!consentActive) {
        return 'Para registrar refeições de forma informativa, preciso da sua autorização. Posso seguir?';
      }
      return 'Refeição registrada no seu acompanhamento calórico informativo. Esse registro não substitui orientação de nutricionista. Quer registrar outra refeição?';
    }

    case 'registrar_calorias': {
      const consentActive = hasCalorieConsent(clienteId);
      if (consentActive) {
        return getCalorieActiveVariant(clienteId);
      }
      return guardrailResult?.safeResponse || 'Para registrar seu acompanhamento calórico de forma informativa, preciso da sua autorização. Posso seguir?';
    }

    case 'recompra': {
      const suggestion = getRebuySuggestion(customer);
      return suggestion.message;
    }

    case 'assinatura':
      return 'A recompra programada está sendo preparada como funcionalidade futura. Por enquanto, posso montar um novo kit e gerar o resumo para WhatsApp toda vez que precisar. Quer montar agora?';

    case 'cross_sell': {
      const suggestions = getCrossSellSuggestion(customer);
      const labels = suggestions.slice(0, 3).map(s => s.label).join(', ');
      return `Temos complementos para acompanhar seus kits: ${labels}. Quer incluir algum no seu pedido?`;
    }

    case 'promocoes':
      return selectResponseVariant({ intent: 'promocoes', segment: seg, variantKey: 'promo', customerId: clienteId });

    case 'indicacao':
      return selectResponseVariant({ intent: 'indicacao', segment: seg, variantKey: 'ref', customerId: clienteId });

    case 'whatsapp':
      return selectResponseVariant({ intent: 'whatsapp', segment: seg, variantKey: 'wa', customerId: clienteId });

    case 'restricao_alimentar':
      return getRestrictionSavedVariant(clienteId);

    case 'cardapio':
      return guardrailResult?.safeResponse || 'Posso te mostrar as principais categorias da Re Leve: combos, proteínas, escondidinhos, sopas, cremes, low carb, sem glúten e sem lactose. Para disponibilidade do dia, confirmo pelo atendimento.';

    case 'compra_coletiva':
      return guardrailResult?.safeResponse || 'Esse pedido pode ser para um volume maior. Você está comprando para você, família ou equipe? Posso ajudar a organizar a melhor opção e encaminhar para atendimento.';

    case 'emagrecimento':
      return guardrailResult?.safeResponse || 'As marmitas podem ajudar na organização da rotina alimentar, mas resultados dependem de vários fatores individuais. Posso te ajudar a organizar sua rotina e registrar seu consumo de forma informativa.';

    case 'dieta_prescricao':
      return guardrailResult?.safeResponse || 'Posso te ajudar a registrar consumo e organizar informações, mas recomendações nutricionais individualizadas devem ser feitas por nutricionista.';

    case 'desconto_alto':
      return guardrailResult?.safeResponse || 'Posso verificar as condições disponíveis. Para algo fora da política de descontos, encaminho ao atendimento.';

    case 'reclamacao':
      return guardrailResult?.safeResponse || 'Para te atender com segurança, vou encaminhar essa situação para atendimento humano. Um momento, por favor.';

    default:
      return selectResponseVariant({ intent: 'fallback', segment: seg, variantKey: 'fb', customerId: clienteId });
  }
}

// ── Motor principal ────────────────────────────────────────────────────────────

export function generateBotResponse({
  message,
  customer,
  scenario,
  selectedKit,
  source,
  chatHistory,
  pendingConsent,
}) {
  const steps = [];
  const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const clienteId = customer?.clienteId || customer?.id || null;
  const segment = (customer?.segmentoComercial || customer?.segmento || customer?.segment || '').toLowerCase();

  // ── 0. Consentimento pendente ─────────────────────────────────────────────────
  if (pendingConsent && clienteId) {
    if (isPendingConsentYes(message)) {
      grantCalorieConsent(clienteId);
      createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.CONSENT_GRANTED, source: 'chat' });
      steps.push('0. Consentimento concedido pelo cliente');
      const consentStatus = getConsentStatus(clienteId);

      // Verificar se há kit para criar plano
      const checkout = getSimulatedCheckoutState(clienteId);
      let caloriePlanStatus = null;
      if (checkout?.kitId) {
        const planResult = createPlanFromConfirmedKit({ clienteId, kit: { kitId: checkout.kitId, id: checkout.kitId }, source: 'consent_granted' });
        caloriePlanStatus = planResult;
        if (planResult.success) {
          createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.CALORIE_PLAN_CREATED, source: 'chat', payload: { kitId: checkout.kitId } });
        }
      }

      const planMsg = caloriePlanStatus?.success
        ? ' Seu plano calórico informativo foi criado com base no kit confirmado.'
        : '';

      return {
        text: `Perfeito! Vou registrar apenas informações úteis para organizar seu acompanhamento.${planMsg} Esse recurso é informativo e não substitui nutricionista. Como posso ajudar agora?`,
        intent: 'registrar_calorias',
        confidence: 'alta',
        guardrails: [],
        activeGuardrailIds: [],
        recommendation: null,
        kit: null,
        memoryUsed: [],
        developerTrace: { steps, decisionReason: 'Cliente concedeu consentimento calórico', consentStatus, calorieTrackingActive: true },
        cta: '',
        shouldEscalateToHuman: false,
        shouldAskConsent: false,
        consentGranted: true,
        consentStatus,
        calorieTrackingActive: true,
        caloriePlanStatus,
        localSessionEvents: getSessionEvents(clienteId),
        restrictionsDeclared: getDeclaredRestrictions(clienteId),
        publicSafe: true,
        timestamp: now,
      };
    }

    if (isPendingConsentNo(message)) {
      denyCalorieConsent(clienteId);
      steps.push('0. Consentimento negado pelo cliente');
      const consentStatus = getConsentStatus(clienteId);
      return {
        text: 'Sem problema! Posso te ajudar com kits para o dia, semana completa ou promoções. O que prefere?',
        intent: 'fallback',
        confidence: 'alta',
        guardrails: [],
        activeGuardrailIds: [],
        recommendation: null,
        kit: null,
        memoryUsed: [],
        developerTrace: { steps, decisionReason: 'Cliente negou consentimento calórico' },
        cta: '',
        shouldEscalateToHuman: false,
        shouldAskConsent: false,
        consentDenied: true,
        consentStatus,
        calorieTrackingActive: false,
        localSessionEvents: getSessionEvents(clienteId),
        restrictionsDeclared: getDeclaredRestrictions(clienteId),
        publicSafe: true,
        timestamp: now,
      };
    }
  }

  // ── 1. Detectar intenção ──────────────────────────────────────────────────────
  const intentResult = detectIntent(message);
  steps.push(`1. Intenção detectada: ${intentResult.intent} (${intentResult.confidence})`);

  // ── 2. Restrição alimentar ────────────────────────────────────────────────────
  let restrictionDetected = [];
  if (clienteId) {
    restrictionDetected = detectDeclaredRestriction(message);
    if (restrictionDetected.length > 0) {
      restrictionDetected.forEach(r => saveDeclaredRestriction(clienteId, r));
      steps.push(`2. Restrição declarada: ${restrictionDetected.map(r => r.label).join(', ')}`);
    } else {
      steps.push('2. Sem restrição declarada nesta mensagem.');
    }
  }

  // ── 3. Memória ────────────────────────────────────────────────────────────────
  const memoryContext = getCustomerMemoryContext(clienteId);
  steps.push(`3. Memória: ${memoryContext.memories.length} registros para ${clienteId || 'anônimo'}`);

  // ── 4. Guardrails ─────────────────────────────────────────────────────────────
  const guardrailResult = analyzeGuardrails({ message, intent: intentResult.intent, customer, memory: memoryContext, params: {} });
  steps.push(`4. Guardrails: ${guardrailResult.triggered ? guardrailResult.guardrailIds.join(', ') : 'nenhum acionado'}`);

  // ── 5. Consentimento calórico ─────────────────────────────────────────────────
  const consentActive = clienteId ? hasCalorieConsent(clienteId) : false;
  const consentStatus = clienteId ? getConsentStatus(clienteId) : 'not_requested';

  let shouldAskConsent = guardrailResult.shouldAskConsent || false;
  if ((intentResult.intent === 'registrar_calorias' || intentResult.intent === 'registrar_refeicao') && !consentActive && clienteId) {
    shouldAskConsent = true;
    requestCalorieConsent(clienteId);
    createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.CONSENT_REQUESTED, source: 'chat' });
    steps.push('5. Consentimento solicitado — intent requer autorização');
  } else {
    steps.push(`5. Consentimento: ${consentStatus}`);
  }

  // ── 6. Alergia grave ──────────────────────────────────────────────────────────
  let forceEscalate = guardrailResult.shouldEscalateToHuman;
  if (clienteId && hasGraveAllergyAlert(clienteId)) {
    forceEscalate = true;
    steps.push('6. Alerta de alergia grave — encaminhar para humano');
  } else {
    steps.push('6. Sem alergia grave');
  }

  // ── 7. Recomendação de kit ────────────────────────────────────────────────────
  let recommendation = null;
  if (!guardrailResult.shouldStopCommercialRecommendation) {
    recommendation = getRecommendedKitForCustomer({ customer, intent: intentResult.intent, memoryContext });
    steps.push(`7. Kit recomendado: ${recommendation?.kitName || 'nenhum'}`);

    if (recommendation && clienteId) {
      createCommercialEvent({
        clienteId,
        type: COMMERCIAL_EVENT_TYPES.KIT_RECOMMENDED,
        source: 'chatbot',
        payload: { kitId: recommendation.kitId, kitName: recommendation.kitName },
      });
    }
  } else {
    steps.push('7. Recomendação bloqueada por guardrail.');
  }

  // ── 8. Jornada comercial ──────────────────────────────────────────────────────
  const journeyStage    = getCustomerJourneyStage(customer);
  const restrictions    = clienteId ? getDeclaredRestrictions(clienteId) : [];
  const nextBestAction  = getNextBestAction({ customer, consentStatus, restrictions, lastIntent: intentResult.intent, lastRecommendation: recommendation });
  const commercialTriggers = getCommercialTriggers({ customer, events: getSessionEvents(clienteId), consentStatus });
  const journeySummary  = getJourneySummary(clienteId);
  steps.push(`8. Jornada: ${journeyStage} → próxima ação: ${nextBestAction.label}`);

  // ── 9. Lógica de confirmar_kit ────────────────────────────────────────────────
  let checkoutResult    = null;
  let caloriePlanStatus = null;
  let confirmedKit      = null;
  let whatsappSummary   = null;

  if (intentResult.intent === 'confirmar_kit' && !guardrailResult.shouldStopCommercialRecommendation) {
    const kitToConfirm = selectedKit || recommendation;
    if (kitToConfirm) {
      checkoutResult = confirmKitSelection({ clienteId, customer, kit: kitToConfirm, source: 'chatbot' });
      confirmedKit   = { kitId: kitToConfirm.kitId || kitToConfirm.id, kitName: kitToConfirm.kitName || kitToConfirm.nome };
      whatsappSummary = checkoutResult?.whatsappSummary;

      if (consentActive) {
        caloriePlanStatus = createPlanFromConfirmedKit({
          clienteId,
          kit: { id: confirmedKit.kitId, kitId: confirmedKit.kitId },
          source: 'kit_confirmed',
        });
        if (caloriePlanStatus?.success) {
          createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.CALORIE_PLAN_CREATED, source: 'chatbot', payload: confirmedKit });
        }
      }
      steps.push(`9. Kit confirmado: ${confirmedKit.kitName} | Plano calórico: ${caloriePlanStatus?.success ? 'criado' : caloriePlanStatus?.reason || 'não criado'}`);
    } else {
      steps.push('9. confirmar_kit — nenhum kit disponível para confirmar');
    }
  } else {
    steps.push('9. Sem ação de confirmação de kit');
  }

  // ── 10. Lógica de registrar_refeicao ─────────────────────────────────────────
  let mealRegistered = null;
  if (intentResult.intent === 'registrar_refeicao' && consentActive && clienteId) {
    const mealType = detectMealType(message);
    if (mealType) {
      mealRegistered = registerMeal({ clienteId, mealType, itemName: `Refeição Re Leve — ${mealType}`, kitId: null });
      createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.MEAL_REGISTERED, source: 'chat', payload: { mealType } });
      steps.push(`10. Refeição registrada: ${mealType}`);
    } else {
      steps.push('10. Tipo de refeição não identificado — solicitando ao usuário');
    }
  } else {
    steps.push('10. Sem registro de refeição nesta mensagem');
  }

  // ── 11. Cross-sell ────────────────────────────────────────────────────────────
  if (intentResult.intent === 'cross_sell' && clienteId) {
    createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.CROSS_SELL_SUGGESTED, source: 'chatbot' });
  }

  // ── 12. Recompra/assinatura ───────────────────────────────────────────────────
  if (intentResult.intent === 'recompra' && clienteId) {
    const rebuy = getRebuySuggestion(customer);
    createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.REBUY_SUGGESTED, source: 'chatbot', payload: rebuy });
    steps.push(`11. Recompra sugerida: ${rebuy.kitId}`);
  }

  // ── 13. Gerar resposta pública ────────────────────────────────────────────────
  const lastConfirmedKit = confirmedKit;
  const publicText = guardrailResult.triggered && guardrailResult.shouldStopCommercialRecommendation
    ? guardrailResult.safeResponse
    : buildPublicResponse({
        intent: intentResult.intent,
        customer,
        kit: recommendation,
        memoryContext,
        guardrailResult,
        clienteId,
        segment,
        lastConfirmedKit,
      });
  steps.push(`13. Resposta gerada (${publicText?.length || 0} chars)`);

  // ── 14. CTA ───────────────────────────────────────────────────────────────────
  let cta = '';
  if (forceEscalate) {
    cta = 'Falar com atendimento';
    if (clienteId) createCommercialEvent({ clienteId, type: COMMERCIAL_EVENT_TYPES.HANDOFF_REQUESTED, source: 'guardrail' });
  } else if (intentResult.intent === 'confirmar_kit') {
    cta = 'Finalizar pelo WhatsApp';
  } else if (intentDetail => intentDetail === 'whatsapp' || intentDetail === 'montar_semana') {
    cta = 'Finalizar pelo WhatsApp';
  } else if (intentResult.intent === 'whatsapp' || intentResult.intent === 'montar_semana') {
    cta = 'Finalizar pelo WhatsApp';
  }

  // ── 15. Evento de sessão ──────────────────────────────────────────────────────
  if (clienteId) {
    addSessionEvent(clienteId, {
      eventType: 'message_processed',
      intent: intentResult.intent,
      guardrailsTriggered: guardrailResult.guardrailIds,
      consentStatus,
    });
  }

  const decisionReason = guardrailResult.triggered
    ? `Guardrail ${guardrailResult.guardrailIds.join('+')} acionado`
    : `Intenção "${intentResult.intent}" → kit "${recommendation?.kitName || 'nenhum'}" por segmento "${customer?.segmentoComercial || customer?.segmento || 'desconhecido'}"`;

  const localSessionEvents = clienteId ? getSessionEvents(clienteId) : [];
  const restrictionsDeclared = clienteId ? getDeclaredRestrictions(clienteId) : [];

  return {
    text: publicText,
    intent: intentResult.intent,
    confidence: intentResult.confidence,
    guardrails: guardrailResult.developerNotes || [],
    activeGuardrailIds: guardrailResult.guardrailIds,
    recommendation,
    kit: recommendation,
    memoryUsed: memoryContext.notesForBot || [],
    developerTrace: {
      steps,
      customerContext: {
        clienteId: clienteId || 'anônimo',
        segmento: customer?.segmentoComercial || customer?.segmento || 'desconhecido',
        source: customer?.fonteValores || 'simulado',
      },
      memoryContext: {
        totalMemories: memoryContext.memories.length,
        restrictions: memoryContext.declaredRestrictions.map(r => r.restriction),
        goals: memoryContext.declaredGoals.map(g => g.goal),
        isSimulated: memoryContext.hasSimulatedMemory,
      },
      intentResult,
      guardrailResult,
      decisionReason,
      blockedByGuardrail: guardrailResult.triggered && guardrailResult.shouldStopCommercialRecommendation,
      escalationRequired: forceEscalate,
      // Etapa 5
      consentStatus,
      calorieTrackingActive: consentActive,
      restrictionsDeclared,
      localSessionEvents,
      restrictionDetectedThisMessage: restrictionDetected,
      // Etapa 6 — jornada comercial
      journeyStage,
      nextBestAction,
      commercialTriggers,
      journeySummary,
      confirmedKit,
      checkoutResult,
      caloriePlanStatus,
      whatsappSummary,
      mealRegistered,
    },
    cta,
    shouldEscalateToHuman: forceEscalate,
    shouldAskConsent,
    // Etapa 5
    consentStatus,
    calorieTrackingActive: consentActive,
    localSessionEvents,
    restrictionsDeclared,
    publicSafe: true,
    timestamp: now,
    // Etapa 6
    journeyStage,
    nextBestAction,
    confirmedKit,
    checkoutResult,
    caloriePlanStatus,
    whatsappSummary,
    mealRegistered,
  };
}

export function buildContext(_customerProfile, _orderHistory) {
  return { ready: true, message: 'Motor Etapa 6 ativo.' };
}
